import { useMemo, useState } from 'react';
import { Alert, Button, Empty, Segmented, Select, Space, Table, Tag, Typography } from 'antd';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { adminLinks } from '../../lib/adminLinks';
import { plain } from '../../lib/labels';
import { ListenerName } from '../../lib/listenerName';
import { formatCallCharge, formatLedgerAmount, formatRupees, ledgerReasonLabel } from '../../lib/money';
import { QueryError } from '../../lib/QueryState';
import { STATUS_COLOR, STATUS_LABEL, type OrderStatus } from '../refunds/paymentLifecycle';

/*
 * The tabs of the user drawer that list THINGS THAT HAPPENED to the user:
 * wallet movements, recharge attempts, calls. Each fetches only when opened
 * (antd Tabs with destroyOnHidden), pages with "Load more", links every row to
 * its own detail, and says so when the request failed. The Overview and the
 * account controls stay in UsersPage.
 */

// ---------------------------------------------------------------- Wallet ---

type LedgerAccount = 'coins' | 'legacy';

interface LedgerRow {
  id: string;
  unit?: 'coin' | 'paise';
  amountCoins?: number | null;
  amountPaise: string | null;
  direction: 'credit' | 'debit';
  reasonCode: string;
  referenceType: string | null;
  referenceId: string | null;
  createdAt: string;
  adminAdjust?: { adminId: string; adminName: string | null; note: string | null } | null;
}
interface LedgerPage {
  items: LedgerRow[];
  nextCursor: string | null;
}

/** Where a ledger row came from, as a link when the panel has a page for it. */
function referenceLink(row: LedgerRow) {
  if (!row.referenceId) return null;
  switch (row.referenceType) {
    case 'payment_order':
    case 'recharge':
      return <Link to={adminLinks.recharge(row.referenceId)}>Open recharge</Link>;
    case 'call_session':
    case 'call':
      return <Link to={adminLinks.call(row.referenceId)}>Open call</Link>;
    default:
      return (
        <Typography.Text type="secondary" style={{ fontSize: 11 }} title={row.referenceId}>
          {plain(row.referenceType ?? '')} {row.referenceId.slice(0, 8)}
        </Typography.Text>
      );
  }
}

export function UserWalletTab({ userId }: { userId: string }) {
  const [account, setAccount] = useState<LedgerAccount>('coins');
  const ledger = useInfiniteQuery({
    queryKey: ['admin', 'user-ledger', userId, account],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<LedgerPage>(`/admin/users/${userId}/wallet/transactions`, {
          params: { account, limit: 50, cursor: pageParam },
        })
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = useMemo(() => ledger.data?.pages.flatMap((p) => p.items) ?? [], [ledger.data]);

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Segmented<LedgerAccount>
        size="small"
        value={account}
        onChange={setAccount}
        options={[
          { label: 'Coin wallet', value: 'coins' },
          { label: 'Legacy wallet (pre-migration)', value: 'legacy' },
        ]}
      />
      {account === 'legacy' && (
        <Alert
          type="info"
          showIcon
          message="Read-only ₹ history of the old paise wallet, frozen at the coin migration. Its balance was carried over as the 'Wallet migration opening balance' coin credit."
        />
      )}
      {ledger.isError && !ledger.data ? (
        <QueryError error={ledger.error} what="the wallet history" onRetry={() => void ledger.refetch()} />
      ) : (
        <>
          <Table
            rowKey="id"
            size="small"
            loading={ledger.isLoading}
            dataSource={rows}
            locale={{ emptyText: 'No wallet movements yet.' }}
            pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100] }}
            columns={[
              { title: 'When', dataIndex: 'createdAt', render: (s: string) => new Date(s).toLocaleString(), width: 170 },
              {
                title: '',
                dataIndex: 'direction',
                render: (d: string) => <Tag color={d === 'credit' ? 'success' : 'error'}>{plain(d)}</Tag>,
                width: 80,
              },
              {
                title: 'Type',
                key: 'reason',
                render: (_: unknown, row: LedgerRow) => (
                  <span title={row.reasonCode}>
                    {ledgerReasonLabel(row.reasonCode, row.direction)}
                    {row.adminAdjust ? (
                      <div style={{ fontSize: 11, opacity: 0.75 }}>
                        by {row.adminAdjust.adminName ?? row.adminAdjust.adminId.slice(0, 8)}
                        {row.adminAdjust.note ? ` — ${row.adminAdjust.note}` : ''}
                      </div>
                    ) : null}
                  </span>
                ),
              },
              { title: 'Amount', key: 'amount', render: (_: unknown, row: LedgerRow) => formatLedgerAmount(row), width: 130 },
              { title: 'Source', key: 'ref', render: (_: unknown, row: LedgerRow) => referenceLink(row), width: 150 },
            ]}
          />
          {ledger.hasNextPage && (
            <div style={{ textAlign: 'center' }}>
              <Button size="small" loading={ledger.isFetchingNextPage} onClick={() => void ledger.fetchNextPage()}>
                Load older movements ({rows.length} loaded)
              </Button>
            </div>
          )}
        </>
      )}
    </Space>
  );
}

// ------------------------------------------------------------- Recharges ---

interface OrderRow {
  id: string;
  amountPaise: string;
  gateway: 'razorpay' | 'google_play';
  gatewayOrderId: string;
  packageName: string | null;
  coins: number | null;
  bonusCoins: number;
  status: OrderStatus;
  failureCode: string | null;
  failureReason: string | null;
  createdAt: string;
}
interface OrdersPage {
  items: OrderRow[];
  nextCursor: string | null;
  total: number;
  filtered: number;
}

const RECHARGE_CHIPS: Array<{ value: OrderStatus | undefined; label: string }> = [
  { value: undefined, label: 'All' },
  { value: 'captured', label: 'Success' },
  { value: 'paid_credit_pending', label: 'Paid — credit pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

export function UserRechargesTab({ userId }: { userId: string }) {
  const [status, setStatus] = useState<OrderStatus | undefined>(undefined);
  const list = useInfiniteQuery({
    queryKey: ['admin', 'payments', 'by-user', userId, status],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<OrdersPage>('/admin/payments/orders', {
          params: { userId, status, limit: 50, cursor: pageParam },
        })
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const matching = list.data?.pages[0]?.filtered;
  // Only `captured` rows moved coins; the sum over the loaded rows is exact once every page is loaded.
  const credited = rows.filter((r) => r.status === 'captured');
  const paidPaise = credited.reduce((a, r) => a + Number(r.amountPaise), 0);
  const coins = credited.reduce((a, r) => a + (r.coins ?? 0), 0);

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Space wrap size={[6, 8]}>
        {RECHARGE_CHIPS.map((c) => (
          <Tag.CheckableTag
            key={c.value ?? 'all'}
            checked={status === c.value}
            onChange={() => setStatus(c.value)}
            style={{ border: '1px solid rgba(255,255,255,0.12)', padding: '2px 8px' }}
          >
            {c.label}
          </Tag.CheckableTag>
        ))}
        {matching !== undefined && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {matching.toLocaleString('en-IN')} attempt{matching === 1 ? '' : 's'}
            {credited.length > 0 && !list.hasNextPage
              ? ` · credited ${formatRupees(String(paidPaise))} = ${coins.toLocaleString('en-IN')} coins`
              : ''}
          </Typography.Text>
        )}
        <Link to={adminLinks.userRecharges(userId)}>Open in Recharges</Link>
      </Space>
      {list.isError && !list.data ? (
        <QueryError error={list.error} what="the recharge history" onRetry={() => void list.refetch()} />
      ) : (
        <>
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={rows}
            locale={{ emptyText: status ? 'No recharge attempts with this status.' : 'No recharge attempts yet.' }}
            pagination={{ pageSize: 25, showSizeChanger: false }}
            columns={[
              { title: 'When', dataIndex: 'createdAt', render: (s: string) => new Date(s).toLocaleString(), width: 170 },
              { title: 'Amount', dataIndex: 'amountPaise', render: (p: string) => formatRupees(p), width: 100 },
              {
                title: 'Coins',
                key: 'coins',
                render: (_: unknown, r: OrderRow) =>
                  r.coins != null ? (
                    <span>
                      {r.coins.toLocaleString('en-IN')}
                      {r.packageName && <div style={{ fontSize: 11, opacity: 0.65 }}>{r.packageName}</div>}
                    </span>
                  ) : (
                    '—'
                  ),
                width: 130,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                render: (v: OrderStatus, r: OrderRow) => (
                  <span>
                    <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v]}</Tag>
                    {r.failureReason && (
                      <div style={{ fontSize: 11, opacity: 0.7 }}>{r.failureReason}</div>
                    )}
                  </span>
                ),
              },
              {
                title: 'Service',
                dataIndex: 'gateway',
                render: (g: string) => (g === 'google_play' ? 'Google Play' : 'Razorpay'),
                width: 105,
              },
              {
                title: '',
                key: 'open',
                width: 80,
                render: (_: unknown, r: OrderRow) => (
                  <Link to={adminLinks.recharge(r.id)}>
                    <Button size="small">Open</Button>
                  </Link>
                ),
              },
            ]}
          />
          {list.hasNextPage && (
            <div style={{ textAlign: 'center' }}>
              <Button size="small" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                Load more ({rows.length} loaded)
              </Button>
            </div>
          )}
        </>
      )}
    </Space>
  );
}

// ----------------------------------------------------------------- Calls ---

interface CallRow {
  id: string;
  listenerId: string;
  listenerHandle?: string;
  listenerDisplayName?: string;
  mode: 'audio' | 'video';
  status: string;
  outcome?: string | null;
  outcomeReason?: string | null;
  durationSeconds: number | null;
  startedAt: string | null;
  totalChargedCoins?: number | null;
  totalChargedPaise: string;
  isVerificationCall?: boolean;
  attemptNo?: number | null;
  callRequestId?: string | null;
}
interface CallsPage {
  items: CallRow[];
  nextCursor: string | null;
  outcomeCounts?: Record<string, number>;
}

type Outcome = 'connected' | 'unconnected' | 'declined' | 'cancelled' | 'no_answer' | 'not_connected' | 'setup_failed';
const OUTCOMES: Array<{ value: Outcome | 'all'; label: string }> = [
  { value: 'all', label: 'All calls' },
  { value: 'connected', label: 'Connected' },
  { value: 'no_answer', label: 'Missed — no answer' },
  { value: 'declined', label: 'Declined by listener' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'not_connected', label: 'Accepted, never connected' },
  { value: 'setup_failed', label: 'Setup failed' },
];

export function UserCallsTab({ userId }: { userId: string }) {
  const [outcome, setOutcome] = useState<Outcome | 'all'>('all');
  const list = useInfiniteQuery({
    queryKey: ['admin', 'calls', 'by-user', userId, outcome],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<CallsPage>('/admin/calls', {
          params: {
            userId,
            outcome: outcome === 'all' ? undefined : outcome,
            limit: 50,
            cursor: pageParam,
            includeOutcomeCounts: pageParam ? undefined : true,
          },
        })
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const counts = list.data?.pages[0]?.outcomeCounts;

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Space wrap>
        <Select<Outcome | 'all'>
          size="small"
          value={outcome}
          onChange={setOutcome}
          style={{ minWidth: 230 }}
          options={OUTCOMES.map((o) => ({
            value: o.value,
            label: counts && counts[o.value] !== undefined ? `${o.label} (${counts[o.value]})` : o.label,
          }))}
        />
        <Link to={adminLinks.userCalls(userId)}>Open in Calls</Link>
      </Space>
      {list.isError && !list.data ? (
        <QueryError error={list.error} what="the call history" onRetry={() => void list.refetch()} />
      ) : rows.length === 0 && !list.isLoading ? (
        <Empty description={outcome === 'all' ? 'No calls yet.' : 'No calls with this outcome.'} />
      ) : (
        <>
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 25, showSizeChanger: false }}
            columns={[
              {
                title: 'When',
                dataIndex: 'startedAt',
                render: (s: string | null) => (s ? new Date(s).toLocaleString() : '—'),
                width: 170,
              },
              {
                title: 'Listener',
                key: 'listener',
                render: (_: unknown, r: CallRow) => (
                  <ListenerName
                    listener={{ displayName: r.listenerDisplayName, handle: r.listenerHandle, id: r.listenerId }}
                    listenerId={r.listenerId}
                    secondary="inline"
                  />
                ),
              },
              { title: 'Mode', dataIndex: 'mode', width: 70, render: (m: string) => plain(m) },
              {
                title: 'Outcome',
                key: 'outcome',
                render: (_: unknown, r: CallRow) => (
                  <span>
                    <Tag>{plain(r.outcome ?? r.status)}</Tag>
                    {r.isVerificationCall && <Tag color="gold">verification</Tag>}
                    {r.callRequestId && (r.attemptNo ?? 1) > 1 && <Tag>fallback ring {r.attemptNo}</Tag>}
                  </span>
                ),
              },
              {
                title: 'Duration',
                dataIndex: 'durationSeconds',
                width: 90,
                render: (s: number | null) => (s ? `${Math.floor(s / 60)}m ${s % 60}s` : '—'),
              },
              { title: 'Charged', key: 'charged', render: (_: unknown, r: CallRow) => formatCallCharge(r), width: 100 },
              {
                title: '',
                key: 'open',
                width: 80,
                render: (_: unknown, r: CallRow) => (
                  <Link to={adminLinks.call(r.id)}>
                    <Button size="small">Open</Button>
                  </Link>
                ),
              },
            ]}
          />
          {list.hasNextPage && (
            <div style={{ textAlign: 'center' }}>
              <Button size="small" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                Load older calls ({rows.length} loaded)
              </Button>
            </div>
          )}
        </>
      )}
    </Space>
  );
}
